import classNames from 'classnames';
import { get, merge } from 'lodash';
import { Node_Type } from '@wix/ricos';
import { IS_POST_ADVANCED_DESIGN_OPTIONS_ENABLED_PATH, LINK_AND_HASHTAG_COLOR, POST_HEADER_FIVE_COLOR, POST_HEADER_FIVE_FONT, POST_HEADER_FOUR_COLOR, POST_HEADER_FOUR_FONT, POST_HEADER_SIX_COLOR, POST_HEADER_SIX_FONT, POST_HEADER_THREE_COLOR, POST_HEADER_THREE_FONT, POST_HEADER_TWO_COLOR, POST_HEADER_TWO_FONT, POST_PAGE_FONT, POST_QUOTES_COLOR, POST_QUOTES_FONT, POST_TEXT_COLOR, POST_TITLE_COLOR, POST_TITLE_FONT, } from '../../../constants/wix-params';
import { getPresetColor, getStyleColor } from '../../../services/colors';
import { extractFontObjAttrs } from '../../../services/fonts';
import { getStyleObject } from './get-style';
import { createToolbarTheme } from './utils';
import stylesBM from './business-manager-theme.scss';
import styles from './themeRicos.scss';
const presetsFallbacks = {
    h1: 'Title',
    h2: 'Page-title',
    h3: 'Heading-XL',
    h4: 'Heading-L',
    h5: 'Heading-M',
    h6: 'Heading-S',
    quote: 'Body-M',
    p: 'Body-M',
};
const postPresets = {
    h1: [POST_TITLE_FONT, POST_TITLE_COLOR],
    h2: [POST_HEADER_TWO_FONT, POST_HEADER_TWO_COLOR],
    h3: [POST_HEADER_THREE_FONT, POST_HEADER_THREE_COLOR],
    h4: [POST_HEADER_FOUR_FONT, POST_HEADER_FOUR_COLOR],
    h5: [POST_HEADER_FIVE_FONT, POST_HEADER_FIVE_COLOR],
    h6: [POST_HEADER_SIX_FONT, POST_HEADER_SIX_COLOR],
    link: [POST_PAGE_FONT, LINK_AND_HASHTAG_COLOR],
    hashtag: [POST_PAGE_FONT, LINK_AND_HASHTAG_COLOR],
    quote: [POST_QUOTES_FONT, POST_QUOTES_COLOR],
    p: [POST_PAGE_FONT, POST_TEXT_COLOR],
};
const fullPostPresets = (section) => {
    const fullPostFont = (font) => `post-full-${section}-${font}Font`;
    const fullPostColor = (color) => `post-${section}-${color}Color`;
    return {
        ...postPresets,
        h1: [fullPostFont('title'), fullPostColor('title')],
        p: [fullPostFont('description'), fullPostColor('description')],
        link: [fullPostFont('description'), fullPostColor('linkHashtag')],
        hashtag: [fullPostFont('description'), fullPostColor('linkHashtag')],
        quote: [fullPostFont('description'), fullPostColor('description')],
    };
};
const layoutToWixParams = ({ appSettings, isMobile, section, isPostPage, isBusinessManager, isEditor, shouldApplyPostDesignInFeed, ...rest }) => {
    const { mobileFontSize, mobileHeaderThreeFontSize, mobileHeaderTwoFontSize, mobileHeaderFourFontSize, mobileHeaderFiveFontSize, mobileHeaderSixFontSize, mobileQuotesFontSize, headerTwoFontSize, headerThreeFontSize, headerFourFontSize, headerFiveFontSize, headerSixFontSize, } = rest;
    const postOrBiz = isPostPage || isBusinessManager || shouldApplyPostDesignInFeed;
    const advancedPostOpts = get(appSettings, IS_POST_ADVANCED_DESIGN_OPTIONS_ENABLED_PATH, false);
    const useBasicFontSize = !postOrBiz || !advancedPostOpts;
    const fontSizePresets = {
        h2: isMobile
            ? mobileHeaderTwoFontSize
            : useBasicFontSize
                ? headerTwoFontSize
                : undefined,
        h3: isMobile
            ? mobileHeaderThreeFontSize
            : useBasicFontSize
                ? headerThreeFontSize
                : undefined,
        h4: isMobile
            ? mobileHeaderFourFontSize
            : useBasicFontSize
                ? headerFourFontSize
                : undefined,
        h5: isMobile
            ? mobileHeaderFiveFontSize
            : useBasicFontSize
                ? headerFiveFontSize
                : undefined,
        h6: isMobile
            ? mobileHeaderSixFontSize
            : useBasicFontSize
                ? headerSixFontSize
                : undefined,
        quote: isMobile ? mobileQuotesFontSize : postOrBiz ? '24px' : undefined,
        p: isMobile ? mobileFontSize : postOrBiz ? '18px' : undefined, // 18px is taken from .blog-post-page-font
    };
    const getLineHeightPresets = (isFallback) => ({
        p: isFallback ? 'normal' : undefined,
        h2: isFallback && isMobile ? '30px' : undefined,
        h3: isFallback && isMobile ? '26px' : undefined,
        h4: isFallback && isMobile ? '20px' : undefined,
        h5: isFallback && isMobile ? '18px' : undefined,
        h6: isFallback && isMobile ? '16px' : undefined,
    });
    /** Sets values depended on Blog's predefined layouts */
    const getLayoutStyle = (element) => {
        if (isPostPage) {
            switch (element) {
                case 'link':
                case 'hashtag':
                    return {
                        // taken from app.scss -> .blog-post-page-font | .blog-post-description-font
                        fontStyle: 'inherit',
                        fontWeight: 'inherit',
                        textDecoration: 'inherit',
                    };
                case 'p':
                    const pageFontStyle = appSettings?.style?.fonts['post-pageFont']?.style;
                    if (pageFontStyle) {
                        return {
                            fontStyle: pageFontStyle?.italic ? 'italic' : 'normal',
                            fontWeight: pageFontStyle?.bold ? 'bold' : 'normal',
                            textDecoration: pageFontStyle?.underline ? 'underline' : 'none',
                        };
                    }
                    return {
                        // taken from app.scss -> .blog-post-page-font | .blog-post-description-font
                        fontStyle: 'inherit',
                        fontWeight: 'inherit',
                        textDecoration: 'inherit',
                    };
                case 'h2':
                case 'h3':
                case 'h4':
                case 'h5':
                case 'h6':
                    if (advancedPostOpts) {
                        const color = postPresets[element][1];
                        return { color: getStyleColor(appSettings, color) };
                    }
                    break;
                case 'quote':
                    return {
                        marginInlineStart: 'var(--ricosQuoteMarginInlineStart)',
                    };
                default:
                    break;
            }
        }
        else {
            if (element === 'quote') {
                if (!isBusinessManager && !shouldApplyPostDesignInFeed) {
                    // taken from theme-ooi.scss -> .advancedQuoteStylesOff
                    return {
                        fontSize: '24px',
                        marginInlineStart: 'var(--ricosQuoteMarginInlineStart)',
                    };
                }
                else {
                    return {
                        lineHeight: 'normal',
                        marginInlineStart: 'var(--ricosQuoteMarginInlineStart)',
                    };
                }
            }
        }
        return {};
    };
    const presets = postOrBiz ? postPresets : fullPostPresets(section);
    const elementMapper = (idx) => {
        const presetEntries = Object.entries(presets);
        return presetEntries.reduce((acc, [tagName, tagFontOrColor]) => ({
            ...acc,
            [tagName]: tagFontOrColor[idx],
        }), {});
    };
    const fontPresets = elementMapper(0);
    const colorPresets = elementMapper(1);
    return {
        fontPresets,
        colorPresets,
        fontSizePresets,
        getLineHeightPresets,
        getLayoutStyle,
    };
};
const correctPx = (value) => {
    if (typeof value === 'string') {
        return `${value.replace(/px/g, '')}px`;
    }
    return `${value}px`;
};
export function createRicosTheme({ appSettings, isMobile, isBusinessManager, section, isPostPage, parentClass, isEditor, shouldApplyPostDesignInFeed, isBlocksPostPage, }) {
    const { textColor, linkHashtagColor, highlightedColor: actionColor, opaqueBackgroundColor: bgColor, ...rest } = getStyleObject(appSettings, isBlocksPostPage);
    const { fontPresets, fontSizePresets, getLineHeightPresets, colorPresets, getLayoutStyle, } = layoutToWixParams({
        appSettings,
        isMobile,
        section,
        isPostPage,
        isBusinessManager,
        isEditor,
        shouldApplyPostDesignInFeed,
        ...rest,
    });
    const getColor = ({ element, presetFallback, isBlocksPostPage, }) => {
        if (isBlocksPostPage) {
            return { color: '#000' };
        }
        const isHeading = /^h[2-6]$/.test(element);
        const isLinkHashtag = element === 'link' || element === 'hashtag';
        const color = getStyleColor(appSettings, colorPresets[element]) ||
            (presetFallback && getPresetColor(appSettings, presetFallback)) ||
            (isHeading ? '#212121' : undefined) ||
            (isLinkHashtag ? linkHashtagColor : undefined);
        return { color };
    };
    /** Font Object from Blog settings */
    const getCustomFontObj = (element) => appSettings?.style?.fonts?.[fontPresets[element]];
    const getStyleByRicosElement = (element, ...additionals) => {
        let fallback = false;
        let fontObj = getCustomFontObj(element);
        if (!fontObj) {
            const textStyleToUse = presetsFallbacks[element];
            // Fetches fallback from Platform Presets
            fontObj = appSettings.textPresets?.[textStyleToUse];
            fallback = true;
        }
        const isLinkOrHashtag = element === 'link' || element === 'hashtag';
        const style = merge(fontObj
            ? {
                ...extractFontObjAttrs(fontObj),
                textDecoration: typeof fontObj.style?.underline === 'boolean'
                    ? fontObj.style.underline
                        ? 'underline'
                        : undefined
                    : fontObj.decoration,
                fontSize: !isLinkOrHashtag && correctPx(fontObj.size),
                lineHeight: undefined,
            }
            : {}, fallback && element in fontSizePresets
            ? // @ts-expect-error
                { fontSize: fontSizePresets[element] }
            : {}, ...additionals);
        if (!style.fontSize) {
            style.fontSize = undefined;
        }
        if (isLinkOrHashtag) {
            style.fontFamily = undefined;
        }
        return style;
    };
    const fallbackColor = isBusinessManager ? '#3899ec' : undefined;
    const blogFontSize = (element) => {
        const fontSize = fontSizePresets[element];
        if (!fontSize) {
            return {};
        }
        return {
            fontSize: typeof fontSize === 'string' ? correctPx(fontSize) : fontSize + 'px',
        };
    };
    const blogLineHeight = (element) => {
        const isFallback = !getCustomFontObj(element);
        const presets = getLineHeightPresets(isFallback);
        const lineHeight = presets[element];
        if (!lineHeight) {
            return {};
        }
        return { lineHeight };
    };
    const customStyles = {
        h1: getStyleByRicosElement('h1', getColor({ element: 'h1', presetFallback: 'color-5', isBlocksPostPage }), getLayoutStyle('h1')),
        h2: getStyleByRicosElement('h2', getColor({ element: 'h2', presetFallback: 'color-5', isBlocksPostPage }), blogFontSize('h2'), blogLineHeight('h2'), getLayoutStyle('h2')),
        h3: getStyleByRicosElement('h3', getColor({ element: 'h3', presetFallback: 'color-5', isBlocksPostPage }), blogFontSize('h3'), blogLineHeight('h3'), getLayoutStyle('h3')),
        h4: getStyleByRicosElement('h4', getColor({ element: 'h4', presetFallback: 'color-5', isBlocksPostPage }), blogFontSize('h4'), blogLineHeight('h4'), getLayoutStyle('h4')),
        h5: getStyleByRicosElement('h5', getColor({ element: 'h5', presetFallback: 'color-5', isBlocksPostPage }), blogFontSize('h5'), blogLineHeight('h5'), getLayoutStyle('h5')),
        h6: getStyleByRicosElement('h6', getColor({ element: 'h6', presetFallback: 'color-5', isBlocksPostPage }), blogFontSize('h6'), blogLineHeight('h6'), getLayoutStyle('h6')),
        link: getStyleByRicosElement('link', getColor({ element: 'link', isBlocksPostPage }), getLayoutStyle('link')),
        hashtag: getStyleByRicosElement('hashtag', getColor({ element: 'hashtag', isBlocksPostPage }), getLayoutStyle('hashtag')),
        quote: getStyleByRicosElement('quote', getColor({
            element: 'quote',
            presetFallback: 'color-5',
            isBlocksPostPage,
        }), { borderColor: linkHashtagColor }, // linkHashtagColor
        isMobile ? blogFontSize('quote') : {}, getLayoutStyle('quote')),
        p: getStyleByRicosElement('p', getColor({ element: 'p', presetFallback: 'color-5', isBlocksPostPage }), isMobile ? blogFontSize('p') : {}, getLayoutStyle('p')),
        footerToolbar: {
            paddingInlineStart: 'var(--ricosFooterPadding)',
            paddingInlineEnd: 'var(--ricosFooterPadding)',
        },
        editor: {
            addPluginButton: {
                positionInlineStart: 'var(--ricosAddPluginButtonStart)',
                plusIconColor: undefined,
            },
        },
        codeBlock: isMobile ? { margin: '15px 0' } : undefined,
    };
    const crossPlatformBreakout = ({ node }) => {
        const isFullWidthImage = node.type === Node_Type.IMAGE &&
            node.imageData?.containerData?.width?.size === 'FULL_WIDTH';
        const isFullWidthGif = node.type === Node_Type.GIF &&
            node.gifData?.containerData?.width?.size === 'FULL_WIDTH';
        const isFullWidthGallery = node.type === Node_Type.GALLERY &&
            node.galleryData?.containerData?.width?.size === 'FULL_WIDTH';
        const isFullWidthVideo = node.type === Node_Type.VIDEO &&
            node.videoData?.containerData?.width?.size === 'FULL_WIDTH';
        if (isFullWidthImage ||
            isFullWidthGif ||
            isFullWidthGallery ||
            isFullWidthVideo) {
            return 'fullWidth';
        }
        return 'normal';
    };
    const breakoutDesktop = ({ node }) => crossPlatformBreakout({ node });
    const breakoutMobile = ({ node }) => {
        const nodesToBreakout = [
            Node_Type.AUDIO,
            Node_Type.BLOCKQUOTE,
            Node_Type.CODE_BLOCK,
            Node_Type.COLLAPSIBLE_ITEM,
            Node_Type.COLLAPSIBLE_LIST,
            Node_Type.EMBED,
            Node_Type.FILE,
            Node_Type.HTML,
            Node_Type.LINK_PREVIEW,
            Node_Type.POLL,
            Node_Type.TABLE,
        ];
        if (nodesToBreakout.includes(node.type)) {
            return 'fullWidth';
        }
        return crossPlatformBreakout({ node });
    };
    const breakout = {
        rule: isMobile ? breakoutMobile : breakoutDesktop,
        normalPadding: {
            start: 'var(--ricosNormalPadding)',
            end: 'var(--ricosNormalPadding)',
        },
        fullWidthPadding: {
            start: 'var(--ricosFullWidthPadding)',
            end: 'var(--ricosFullWidthPadding)',
        },
    };
    // OOI Mobile editor uses 999999999 ;o
    const zIndex = 9999999999;
    const settingsStyles = {
        modals: isMobile
            ? {
                toolbarZIndex: zIndex,
                popupZIndex: zIndex,
                drawerZIndex: zIndex,
                dialogZIndex: zIndex,
                tooltipZIndex: zIndex,
                notificationZIndex: zIndex,
            }
            : undefined,
    };
    return {
        palette: {
            bgColor,
            textColor,
            actionColor,
            fallbackColor,
        },
        paletteConfig: {
            settingsActionColor: fallbackColor,
            focusActionColor: fallbackColor,
        },
        customStyles,
        parentClass,
        breakout,
        settingsStyles,
    };
}
export function getCssOverride({ isViewer, version, isCompact, readOnly: isReadOnly, isBusinessManager, appSettings, } = {}) {
    const { dividerContainer, wrapper, readOnly, sizeCompact, text, orderedListItem, orderedListContainer, unorderedListItem, unorderedListContainer, toolbarButtonWrapper, toolbarButton, footerToolbar, dividerBorderColor, headerTwo, headerTwoFontSize, headerThree, headerThreeFontSize, left, center, right, justify, ...rest } = styles;
    const defaultToolbarTheme = {
        toolbarButton_wrapper: toolbarButtonWrapper,
        toolbarButton,
    };
    const advancedPostOpts = get(appSettings, IS_POST_ADVANCED_DESIGN_OPTIONS_ENABLED_PATH, false);
    const headerTwoStyles = !advancedPostOpts &&
        classNames('blog-post-title-font', headerTwo, headerTwoFontSize);
    const headerThreeStyles = !advancedPostOpts &&
        classNames('blog-post-title-font', headerThree, headerThreeFontSize);
    const theme = {
        ...rest,
        ...createToolbarTheme('footer', {
            ...defaultToolbarTheme,
            toolbar: classNames(footerToolbar, dividerBorderColor),
        }),
        headerTwo: headerTwoStyles,
        headerThreeStyles,
        wrapper: classNames(wrapper, {
            [readOnly]: isReadOnly,
            [sizeCompact]: isCompact,
        }),
        readOnly,
        sizeCompact,
        'divider-container': isViewer && dividerContainer,
        text,
        left: isViewer && classNames(left, text),
        center: isViewer && classNames(center, text),
        right: isViewer && classNames(right, text),
        justify: isViewer && classNames(justify, text),
        orderedList: classNames(text, /7.16.2/.test(version ?? '') && isViewer ? orderedListItem : undefined),
        orderedListContainer,
        unorderedList: classNames(text, unorderedListItem),
        unorderedListContainer,
    };
    if (isBusinessManager) {
        // copied from business-manager-theme.js
        const { input, label, textarea, textInputInputInvalid, imageSettingsSection, galleryImageSettingsSection, imageSettingsLabel, } = stylesBM;
        return {
            ...theme,
            textInput_input: input,
            textInput_input_invalid: textInputInputInvalid,
            inputWithLabel_input: input,
            inputWithLabel_textArea: textarea,
            inputWithLabel_label: label,
            imageSettingsSection,
            imageSettings_section: galleryImageSettingsSection,
            galleryImageSettings_section: galleryImageSettingsSection,
            layoutsSelector_label: label,
            layoutsSelector_tile_label: label,
            radioGroupHorizontal_title: label,
            sliderWithInput_label: label,
            imageRatioSelector_label: label,
            imageRatioSelector_ratioLabel: label,
            thumbnailPlacementSelector_label: label,
            imageSettingsLabel,
        };
    }
    return theme;
}
//# sourceMappingURL=themeRicos.js.map